import React, { createContext, useCallback, useMemo, useState } from 'react';
import { Alert, Button, Slide, Snackbar } from "@mui/material";

export const ErrorContext = createContext({
    data: {
        show: false,
        clickable: false,
        message: '',
        buttonTitle: '',
        duration: 4000,
        severity: 'error',
        onClick: () => {}
    },
    showError: () => {},
    showSuccess: () => {},
    showWarning: () => {},
    showInfo: () => {},
    hideError: () => {}
});

const defaultState = {
    show: false,
    clickable: false,
    message: '',
    buttonTitle: '',
    duration: 4000,
    severity: 'error',
    onClick: () => {}
};

function SlideTransition(props) {
    return <Slide {...props} direction="up" />;
}

// تشخیص خودکار شدت پیام بر اساس کلمات کلیدی فارسی در صورت عدم تعیین صریح
function detectSeverity(message, explicitSeverity) {
    if (explicitSeverity) return explicitSeverity;
    if (!message || typeof message !== 'string') return 'error';

    const lower = message.toLowerCase();
    if (lower.includes("موفقیت") || lower.includes("انجام شد") || lower.includes("ثبت شد") || lower.includes("تایید شد")) {
        return 'success';
    }
    if (lower.includes("صبر کنید") || lower.includes("توجه") || lower.includes("هشدار") || lower.includes("نامعتبر")) {
        return 'warning';
    }
    return 'error';
}

const GympinPagesProvider = ({ children }) => {
    const [snackState, setSnackState] = useState(defaultState);

    const hideError = useCallback(() => {
        setSnackState(prev => ({ ...prev, show: false }));
    }, []);

    const showError = useCallback((errorInput) => {
        if (!errorInput) return;

        let errorObj = {};
        if (typeof errorInput === 'string') {
            errorObj = { message: errorInput };
        } else if (typeof errorInput === 'object') {
            errorObj = { ...errorInput };
        }

        if (!errorObj.message) return;

        const severity = detectSeverity(errorObj.message, errorObj.severity);
        const duration = errorObj.duration || 4000;

        setSnackState({
            show: true,
            clickable: Boolean(errorObj.clickable && errorObj.buttonTitle),
            message: errorObj.message,
            buttonTitle: errorObj.buttonTitle || '',
            duration,
            severity,
            onClick: errorObj.onClick || (() => {})
        });
    }, []);

    const showSuccess = useCallback((input) => {
        const obj = typeof input === 'string' ? { message: input } : input;
        showError({ ...obj, severity: 'success' });
    }, [showError]);

    const showWarning = useCallback((input) => {
        const obj = typeof input === 'string' ? { message: input } : input;
        showError({ ...obj, severity: 'warning' });
    }, [showError]);

    const showInfo = useCallback((input) => {
        const obj = typeof input === 'string' ? { message: input } : input;
        showError({ ...obj, severity: 'info' });
    }, [showError]);

    const handleActionButtonClick = useCallback(() => {
        if (snackState.onClick) {
            snackState.onClick();
        }
        hideError();
    }, [snackState, hideError]);

    const contextValue = useMemo(() => ({
        showError,
        showSuccess,
        showWarning,
        showInfo,
        hideError
    }), [showError, showSuccess, showWarning, showInfo, hideError]);

    return (
        <ErrorContext.Provider value={contextValue}>
            {children}

            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackState.show}
                autoHideDuration={snackState.duration}
                onClose={hideError}
                TransitionComponent={SlideTransition}
                sx={{
                    bottom: { xs: 85, sm: 40 },
                    zIndex: 2000,
                    maxWidth: "92vw",
                    width: "auto"
                }}
            >
                <Alert
                    onClose={hideError}
                    severity={snackState.severity || "error"}
                    variant="filled"
                    elevation={6}
                    action={
                        snackState.clickable ? (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleActionButtonClick}
                                sx={{
                                    fontFamily: "IRANSans-web, Tahoma, sans-serif",
                                    fontWeight: 700,
                                    fontSize: "0.82rem",
                                    whiteSpace: "nowrap",
                                    bgcolor: "rgba(255,255,255,0.22)",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.34)" },
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: "auto",
                                    marginInlineEnd: 0.5
                                }}
                            >
                                {snackState.buttonTitle}
                            </Button>
                        ) : undefined
                    }
                    sx={{
                        width: "100%",
                        maxWidth: { xs: "94vw", sm: 560 },
                        display: "flex",
                        alignItems: "center",
                        columnGap: 2,
                        fontFamily: "IRANSans-web, Tahoma, sans-serif",
                        borderRadius: 3,
                        px: { xs: 1.75, sm: 2.25 },
                        py: { xs: 1.15, sm: 1.35 },
                        fontSize: "0.92rem",
                        fontWeight: 500,
                        boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
                        "& .MuiAlert-icon": {
                            fontSize: "1.45rem",
                            padding: 0,
                            margin: 0,
                            marginInlineEnd: 0,
                            opacity: 0.98,
                            alignItems: "center",
                            flexShrink: 0
                        },
                        "& .MuiAlert-message": {
                            direction: "rtl",
                            textAlign: "right",
                            lineHeight: 1.75,
                            padding: 0,
                            py: 0.25,
                            flex: "1 1 auto",
                            minWidth: 0
                        },
                        "& .MuiAlert-action": {
                            margin: 0,
                            padding: 0,
                            marginInlineStart: 0,
                            alignItems: "center",
                            gap: 0.75,
                            flexShrink: 0
                        },
                        "& .MuiAlert-action .MuiIconButton-root": {
                            padding: 0.75,
                            marginInlineStart: 0.25
                        }
                    }}
                >
                    {snackState.message}
                </Alert>
            </Snackbar>
        </ErrorContext.Provider>
    );
};

export default GympinPagesProvider;
